import { generatePath, matchPath } from "react-router-dom";
import { useLocation, useNavigate } from "react-router-dom-v5-compat";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { Project, Tenant, Tool, ToolVersion } from "graphql/_Types";
import { create, StoreApi, UseBoundStore, useStore } from "zustand";
import assert from "assert";
import { ContextOutOfBoundsError } from "providers/ContextOutOfBoundsError";
import { ExtractRouteParams } from "react-router";

const TOOL_BASE = "/:tenantKey/tool/:toolId/" as const;

type StaticRoute = {
  path: string;
  navigateTo: () => void;
  navigateToReplace: () => void;
};

export type DynamicRoute<
  Path extends string,
  Params extends ExtractRouteParams<Path>,
  Options = unknown,
> = {
  parameterizedPath: Path;
  dynamicPath: (params: Params, options?: Options) => StaticRoute;
};

interface RouteMapPrivateStoreState {
  navigate: ReturnType<typeof useNavigate>;
  routeMap: {
    PROJECT: DynamicRoute<
      "/:tenantKey/project/:projectKey",
      { tenantKey: Tenant["key"]; projectKey: Project["key"] }
    >;
    PROJECT_TABLE: DynamicRoute<
      "/:tenantKey/project/:projectKey/table/:tableKey",
      {
        tenantKey: Tenant["key"];
        projectKey: Project["key"];
        tableKey: string;
      }
    >;
    CHANGELOG: StaticRoute;
    DASHBOARD: StaticRoute;

    HELP: StaticRoute;
    ROOT: StaticRoute;
    SETTINGS: StaticRoute;
    CREATE_PERSONAL_SPACE: StaticRoute;
    ORGANIZATION: DynamicRoute<"/:tenantKey", { tenantKey: Tenant["key"] }>;
    ORGANIZATION_TOOLS: DynamicRoute<
      "/:tenantKey/tools",
      { tenantKey: Tenant["key"] }
    >;
    ORGANIZATION_CONTAINERS: DynamicRoute<
      "/:tenantKey/containers",
      { tenantKey: Tenant["key"] }
    >;
    ORGANIZATION_USAGE: DynamicRoute<
      "/:tenantKey/usage",
      { tenantKey: Tenant["key"] }
    >;
    ORGANIZATION_PEOPLE: DynamicRoute<
      "/:tenantKey/people",
      { tenantKey: Tenant["key"] }
    >;
    PROJECT_FILES: DynamicRoute<
      "/:tenantKey/project/:projectKey/files",
      { projectKey: Project["key"]; tenantKey: Tenant["key"] }
    >;
    PROJECT_TASKS: DynamicRoute<
      "/:tenantKey/project/:projectKey/tasks",
      { projectKey: Project["key"]; tenantKey: Tenant["key"] }
    >;
    PROJECT_ACCESS: DynamicRoute<
      "/:tenantKey/project/:projectKey/access",
      { projectKey: Project["key"]; tenantKey: Tenant["key"] }
    >;
    PROJECT_SETTINGS: DynamicRoute<
      "/:tenantKey/project/:projectKey/settings",
      { projectKey: Project["key"]; tenantKey: Tenant["key"] }
    >;
    TOOL: DynamicRoute<
      typeof TOOL_BASE,
      { tenantKey: Tenant["key"]; toolId: Tool["id"] }
    >;
    TOOL_TASKS: DynamicRoute<
      `${typeof TOOL_BASE}tasks`,
      { tenantKey: Tenant["key"]; toolId: Tool["id"] }
    >;
    TOOL_FILES: DynamicRoute<
      `${typeof TOOL_BASE}files`,
      { tenantKey: Tenant["key"]; toolId: Tool["id"] }
    >;
    TOOL_SETTINGS: DynamicRoute<
      `${typeof TOOL_BASE}settings`,
      { tenantKey: Tenant["key"]; toolId: Tool["id"] }
    >;
    TOOL_EDITOR: DynamicRoute<
      "/:tenantKey/tool/:toolId/edit/:toolVersionId",
      {
        tenantKey: Tenant["key"];
        toolId: Tool["id"];
        toolVersionId: ToolVersion["id"];
      }
    >;
  };
  isRouteMatch: (path: string) => boolean;
}

export type RouteMapPublicStoreState = Omit<
  RouteMapPrivateStoreState,
  "navigate" | "navigateTo" | "appendParamToPath"
>;

const RouteMapContext = createContext<
  UseBoundStore<StoreApi<RouteMapPublicStoreState>> | undefined
>(undefined);

export const RouteMapProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();

  const navigate = useNavigate();
  const [store] = useState(() =>
    create<RouteMapPrivateStoreState>((_, get) => {
      const navigateTo = (path: string, mode: "push" | "replace" = "push") => {
        mode === "push"
          ? get().navigate(path)
          : get().navigate(path, { replace: true });
      };

      const generateStaticRoute = (path: string) => {
        return {
          path,
          navigateTo: () => navigateTo(path),
          navigateToReplace: () => navigateTo(path, "replace"),
        };
      };

      const generateDynamicRoute = <T extends string>(parameterizedPath: T) => {
        return {
          parameterizedPath,
          dynamicPath: (params: ExtractRouteParams<T>) => {
            const path = generatePath(parameterizedPath, params);
            return generateStaticRoute(path);
          },
        };
      };

      return {
        isRouteMatch: (path) =>
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
          matchPath(location.pathname, { path })?.isExact ?? false,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        navigate,
        routeMap: {
          CHANGELOG: generateStaticRoute("/changelog"),
          DASHBOARD: generateStaticRoute("/dashboard"),
          HELP: generateStaticRoute("/help"),
          ROOT: generateStaticRoute("/"),
          SETTINGS: generateStaticRoute("/settings"),
          CREATE_PERSONAL_SPACE: generateStaticRoute("/create-personal-space"),
          ORGANIZATION: generateDynamicRoute("/:tenantKey"),
          ORGANIZATION_TOOLS: generateDynamicRoute("/:tenantKey/tools"),
          ORGANIZATION_CONTAINERS: generateDynamicRoute(
            "/:tenantKey/containers",
          ),
          ORGANIZATION_USAGE: generateDynamicRoute("/:tenantKey/usage"),
          ORGANIZATION_PEOPLE: generateDynamicRoute("/:tenantKey/people"),
          PROJECT_FILES: generateDynamicRoute(
            "/:tenantKey/project/:projectKey/files",
          ),
          PROJECT_TASKS: generateDynamicRoute(
            "/:tenantKey/project/:projectKey/tasks",
          ),
          PROJECT_ACCESS: generateDynamicRoute(
            "/:tenantKey/project/:projectKey/access",
          ),
          PROJECT_SETTINGS: generateDynamicRoute(
            "/:tenantKey/project/:projectKey/settings",
          ),
          PROJECT_TABLE: generateDynamicRoute(
            "/:tenantKey/project/:projectKey/table/:tableKey",
          ),
          PROJECT: generateDynamicRoute("/:tenantKey/project/:projectKey"),
          TOOL_EDITOR: generateDynamicRoute(
            "/:tenantKey/tool/:toolId/edit/:toolVersionId",
          ),
          TOOL: generateDynamicRoute(TOOL_BASE),
          TOOL_TASKS: generateDynamicRoute(`${TOOL_BASE}tasks` as const),
          TOOL_FILES: generateDynamicRoute(`${TOOL_BASE}files` as const),
          TOOL_SETTINGS: generateDynamicRoute(`${TOOL_BASE}settings` as const),
        },
      };
    }),
  );

  /**
   * isRouteMatch should be reactive so update store when history changes
   * Components will only subscribe to their specific route match when you call this in a selector
   */
  useEffect(() => {
    store.setState({
      ...store.getState(),
      isRouteMatch: (path) =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        matchPath(location.pathname, { path })?.isExact ?? false,
    });
  }, [store, location]);

  /**
   * Keep history in store current
   */
  useEffect(() => {
    store.setState({ ...store.getState(), navigate });
  }, [navigate, store]);

  return (
    <RouteMapContext.Provider value={store}>
      {children}
    </RouteMapContext.Provider>
  );
};

export const useRouteMapContext = <S,>(
  selector: (state: RouteMapPublicStoreState) => S,
  comparator?: (a: S, b: S) => boolean,
): S => {
  const store = useContext(RouteMapContext);
  assert(store !== undefined, new ContextOutOfBoundsError("RouteMapContext"));
  return useStore(store, selector, comparator);
};
